import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { googleSignIn } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';

interface GoogleAuthButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const GOOGLE_SCRIPT_ID = 'google-identity-services';
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

const loadGoogleScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.getElementById(GOOGLE_SCRIPT_ID)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
};

declare global {
  interface Window {
    google: any;
  }
}

export const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({
  onSuccess,
  onError,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);

  const handleCallback = async (response: any) => {
    const idToken = response?.credential;
    if (!idToken) {
      onError?.('No credential received from Google');
      return;
    }

    const result = await dispatch(googleSignIn(idToken));
    if (googleSignIn.fulfilled.match(result)) {
      onSuccess?.();
      navigate('/');
    } else {
      onError?.(result.payload as string);
    }
  };

  const initGoogle = () => {
    if (!window.google) {
      onError?.('Google Identity Services not loaded');
      return;
    }

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

    if (!clientId) {
      onError?.('Google OAuth client ID not configured');
      return;
    }

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: handleCallback,
    });

    if (buttonRef.current) {
      buttonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(
        buttonRef.current,
        { theme: 'outline', size: 'large', shape: 'rectangular', width: 300 }
      );
    }
  };

  useEffect(() => {
    if (scriptLoadedRef.current) {
      initGoogle();
      return;
    }

    loadGoogleScript()
      .then(() => {
        scriptLoadedRef.current = true;
        initGoogle();
      })
      .catch((err) => {
        onError?.(err.message || 'Failed to load Google Identity Services');
      });
  }, []);

  return (
    <Box
      ref={buttonRef}
      sx={{
        mt: 1,
        mb: 2,
        display: 'flex',
        justifyContent: 'center',
        '& > div': {
          width: '100%',
        },
      }}
    />
  );
};

export default GoogleAuthButton;
